import { useEffect, useState, useRef, useCallback, type ChangeEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  Plus,
  Pencil,
  Eye,
  Trash2,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Upload,
  RotateCcw,
  Power,
  Download,
  FileUp,
  EllipsisVertical,
  FileJson,
  LayoutGrid,
  ExternalLink,
  Copy,
} from 'lucide-react'
import { templateApi, webhookSubscriptionApi } from '@/api/services'
import { useToast } from '@/context/ToastContext'
import { Button, buttonVariants } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Input } from '@/components/ui/Input'
import { Label, Spinner, EmptyState } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import { defaultCloneCode, defaultCloneKey, defaultCloneName } from '@/lib/cloneDefaults'
import { fetchDataSourcesByKeys } from '@/lib/dataSourceParameters'
import { toastApiError } from '@/lib/apiError'
import type { CloneTemplateDataSourceNaming, TemplateExportPack, TemplateSummary, WebhookSubscription } from '@/types'

const PAGE_SIZE = 12
const TEMPLATE_PUBLISHED_EVENT = 'template.published'
const TEMPLATE_ACTIVE_CHANGED_EVENT = 'template.active.changed'

export default function TemplatesPage() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const [templates, setTemplates] = useState<TemplateSummary[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [publishing, setPublishing] = useState(false)
  const [activating, setActivating] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
  const [aggregatedTags, setAggregatedTags] = useState<string[]>([])
  const [publishTemplateIds, setPublishTemplateIds] = useState<string[]>([])
  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [subscribers, setSubscribers] = useState<WebhookSubscription[]>([])
  const [subscribersLoading, setSubscribersLoading] = useState(false)
  const [sendToAllSubscribers, setSendToAllSubscribers] = useState(true)
  const [selectedSubscriberIds, setSelectedSubscriberIds] = useState<Set<string>>(new Set())
  const [activateTemplateIds, setActivateTemplateIds] = useState<string[]>([])
  const [activateModalOpen, setActivateModalOpen] = useState(false)
  const [activeSubscribers, setActiveSubscribers] = useState<WebhookSubscription[]>([])
  const [activeSubscribersLoading, setActiveSubscribersLoading] = useState(false)
  const [sendToAllActiveSubscribers, setSendToAllActiveSubscribers] = useState(true)
  const [selectedActiveSubscriberIds, setSelectedActiveSubscriberIds] = useState<Set<string>>(new Set())
  const [activeToggleValue, setActiveToggleValue] = useState(true)
  const [activeChangedSubscribersAvailable, setActiveChangedSubscribersAvailable] = useState(false)
  const [activeChangedSubscribersChecking, setActiveChangedSubscribersChecking] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [exportingPack, setExportingPack] = useState(false)
  const [importPackModalOpen, setImportPackModalOpen] = useState(false)
  const [pendingImportPack, setPendingImportPack] = useState<TemplateExportPack | null>(null)
  const [importConflictUpsert, setImportConflictUpsert] = useState(true)
  const [importingPack, setImportingPack] = useState(false)
  const [cloneModalOpen, setCloneModalOpen] = useState(false)
  const [cloneSourceTemplateId, setCloneSourceTemplateId] = useState<string | null>(null)
  const [cloneName, setCloneName] = useState('')
  const [cloneCode, setCloneCode] = useState('')
  const [cloneDataSourceKeys, setCloneDataSourceKeys] = useState<CloneTemplateDataSourceNaming[]>([])
  const [cloneMissingKeys, setCloneMissingKeys] = useState<string[]>([])
  const [cloneOpeningId, setCloneOpeningId] = useState<string | null>(null)
  const [cloning, setCloning] = useState(false)
  const searchTimer = useRef<ReturnType<typeof setTimeout>>()
  const searchRef = useRef<HTMLInputElement>(null)
  const importPackFileRef = useRef<HTMLInputElement>(null)

  // Debounce search input
  useEffect(() => {
    clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 350)
    return () => clearTimeout(searchTimer.current)
  }, [search])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await templateApi.getAll({
        filter: debouncedSearch || undefined,
        includeDeleted: showDeleted,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        tags: selectedTags.size > 0 ? Array.from(selectedTags) : undefined,
      })
      setTemplates(res.items)
      setTotalCount(res.totalCount)
      setTotalPages(res.totalPages)
      setSelectedIds(new Set())
    } catch (e: unknown) {
      toastApiError(toast, e, 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, page, showDeleted, selectedTags, toast])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!publishModalOpen) return
    let cancelled = false
    ;(async () => {
      setSubscribersLoading(true)
      try {
        const res = await webhookSubscriptionApi.getAll({
          eventType: TEMPLATE_PUBLISHED_EVENT,
          isEnabled: true,
          activeOnly: true,
          pageNumber: 1,
          pageSize: 100,
        })
        if (!cancelled) setSubscribers(res.items)
      } catch (e: unknown) {
        if (!cancelled) toastApiError(toast, e, 'Failed to load subscribers')
      } finally {
        if (!cancelled) setSubscribersLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [publishModalOpen, toast])

  useEffect(() => {
    if (!activateModalOpen) return
    let cancelled = false
    ;(async () => {
      setActiveSubscribersLoading(true)
      try {
        const res = await webhookSubscriptionApi.getAll({
          eventType: TEMPLATE_ACTIVE_CHANGED_EVENT,
          isEnabled: true,
          activeOnly: true,
          pageNumber: 1,
          pageSize: 100,
        })
        if (!cancelled) setActiveSubscribers(res.items)
      } catch (e: unknown) {
        if (!cancelled) toastApiError(toast, e, 'Failed to load active-change subscribers')
      } finally {
        if (!cancelled) setActiveSubscribersLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [activateModalOpen, toast])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setActiveChangedSubscribersChecking(true)
      try {
        const res = await webhookSubscriptionApi.getAll({
          eventType: TEMPLATE_ACTIVE_CHANGED_EVENT,
          isEnabled: true,
          activeOnly: true,
          pageNumber: 1,
          pageSize: 1,
        })
        if (!cancelled) setActiveChangedSubscribersAvailable(res.totalCount > 0)
      } catch (e: unknown) {
        if (!cancelled) {
          setActiveChangedSubscribersAvailable(false)
          toastApiError(toast, e, 'Failed to check active-change subscribers')
        }
      } finally {
        if (!cancelled) setActiveChangedSubscribersChecking(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [toast])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res =
          selectedIds.size === 0
            ? await templateApi.getTags({ includeDeleted: showDeleted })
            : await templateApi.getTags({ templateIds: Array.from(selectedIds), includeDeleted: showDeleted })
        if (!cancelled) setAggregatedTags(res.tags)
      } catch {
        if (!cancelled) setAggregatedTags([])
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedIds, showDeleted])

  async function confirmDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await templateApi.delete(deleteTarget.id)
      toast(`"${deleteTarget.name}" deleted`)
      setDeleteTarget(null)
      load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  async function restoreTemplate(template: TemplateSummary) {
    setRestoringId(template.id)
    try {
      await templateApi.restore(template.id)
      toast(`"${template.name}" restored`)
      await load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Restore failed')
    } finally {
      setRestoringId(null)
    }
  }

  function openPublishModal(templateIds: string[]) {
    if (templateIds.length === 0) return
    setPublishTemplateIds(templateIds)
    setSendToAllSubscribers(true)
    setSelectedSubscriberIds(new Set())
    setPublishModalOpen(true)
  }

  function closePublishModal() {
    if (publishing) return
    setPublishModalOpen(false)
    setPublishTemplateIds([])
  }

  function toggleSubscriber(id: string) {
    setSendToAllSubscribers(false)
    setSelectedSubscriberIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function openActivateModal(templateIds: string[]) {
    if (templateIds.length === 0) return
    setActivateTemplateIds(templateIds)
    setActiveToggleValue(true)
    setSendToAllActiveSubscribers(true)
    setSelectedActiveSubscriberIds(new Set())
    setActivateModalOpen(true)
  }

  function closeActivateModal() {
    if (activating) return
    setActivateModalOpen(false)
    setActivateTemplateIds([])
  }

  function toggleActiveSubscriber(id: string) {
    setSendToAllActiveSubscribers(false)
    setSelectedActiveSubscriberIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function publishTemplates(templateIds: string[], webhookSubscriptionIds?: string[]) {
    if (templateIds.length === 0) return
    if (webhookSubscriptionIds && webhookSubscriptionIds.length === 0) {
      toast('Select at least one subscriber or send to all', 'error')
      return
    }
    setPublishing(true)
    try {
      await templateApi.publish(templateIds, webhookSubscriptionIds)
      toast(`Published ${templateIds.length} template${templateIds.length !== 1 ? 's' : ''}`)
      await load()
      setSelectedIds(new Set())
      setPublishModalOpen(false)
      setPublishTemplateIds([])
    } catch (e: unknown) {
      toastApiError(toast, e, 'Publish failed')
    } finally {
      setPublishing(false)
    }
  }

  async function exportTemplatePackJson(templateIds: string[]) {
    if (templateIds.length === 0) return
    setExportingPack(true)
    try {
      const pack = await templateApi.exportPack(templateIds)
      const name = `report-studio-template-pack-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`
      const blob = new Blob([JSON.stringify(pack, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      a.click()
      URL.revokeObjectURL(url)
      toast(`Exported ${pack.templates.length} template${pack.templates.length !== 1 ? 's' : ''} (${pack.dataSources.length} data source${pack.dataSources.length !== 1 ? 's' : ''})`)
      if (pack.missingDataSourceKeys?.length) {
        toast(`Warning: ${pack.missingDataSourceKeys.length} referenced data source key(s) were not found in the database.`, 'error')
      }
    } catch (e: unknown) {
      toastApiError(toast, e, 'Export failed')
    } finally {
      setExportingPack(false)
    }
  }

  function closeCloneModal() {
    setCloneModalOpen(false)
    setCloneSourceTemplateId(null)
    setCloneName('')
    setCloneCode('')
    setCloneDataSourceKeys([])
    setCloneMissingKeys([])
  }

  async function openCloneModal(templateId: string) {
    setCloneOpeningId(templateId)
    try {
      const template = await templateApi.getById(templateId)
      const requiredKeys = template.requiredDataSourceKeys ?? []
      const sources = await fetchDataSourcesByKeys(requiredKeys)
      const missingKeys = requiredKeys.filter(k => !sources.some(s => s.key === k))

      setCloneSourceTemplateId(templateId)
      setCloneName(defaultCloneName(template.name))
      setCloneCode(defaultCloneCode(template.code))
      setCloneDataSourceKeys(
        requiredKeys.map(sourceKey => ({
          sourceKey,
          newKey: defaultCloneKey(sourceKey),
        }))
      )
      setCloneMissingKeys(missingKeys)
      setCloneModalOpen(true)
    } catch (e: unknown) {
      toastApiError(toast, e, 'Failed to load template for clone')
    } finally {
      setCloneOpeningId(null)
    }
  }

  async function confirmCloneTemplate() {
    if (!cloneSourceTemplateId) return
    if (!cloneName.trim() || !cloneCode.trim()) {
      toast('Name and code are required', 'error')
      return
    }
    if (cloneCode.trim().length > 8) {
      toast('Code must be 8 characters max', 'error')
      return
    }
    if (cloneMissingKeys.length > 0) {
      toast('Cannot clone: one or more data sources were not found', 'error')
      return
    }

    setCloning(true)
    try {
      const result = await templateApi.clone(cloneSourceTemplateId, {
        name: cloneName.trim(),
        code: cloneCode.trim().toUpperCase(),
        dataSources: cloneDataSourceKeys.map(x => ({
          sourceKey: x.sourceKey,
          newKey: x.newKey.trim().toLowerCase().replace(/\s+/g, '_'),
        })),
      })
      toast(`Cloned as "${result.name}"`)
      closeCloneModal()
      await load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Clone failed')
    } finally {
      setCloning(false)
    }
  }

  function openImportPackFilePicker() {
    importPackFileRef.current?.click()
  }

  async function onImportPackFileChosen(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    try {
      const text = await file.text()
      const parsed = JSON.parse(text) as unknown
      if (typeof parsed !== 'object' || parsed === null) {
        toast('Invalid JSON file', 'error')
        return
      }
      const p = parsed as Record<string, unknown>
      if (p.schemaVersion !== 1) {
        toast('Unsupported pack: schemaVersion must be 1', 'error')
        return
      }
      if (!Array.isArray(p.templates) || !Array.isArray(p.dataSources)) {
        toast('Invalid pack: expected templates and dataSources arrays', 'error')
        return
      }
      setPendingImportPack(parsed as TemplateExportPack)
      setImportConflictUpsert(true)
      setImportPackModalOpen(true)
    } catch (e: unknown) {
      toastApiError(toast, e, 'Could not read import file')
    }
  }

  function closeImportPackModal() {
    if (importingPack) return
    setImportPackModalOpen(false)
    setPendingImportPack(null)
  }

  async function confirmImportPack() {
    if (!pendingImportPack) return
    setImportingPack(true)
    try {
      const res = await templateApi.importPack(
        pendingImportPack,
        importConflictUpsert ? 'upsert' : 'fail'
      )
      const parts = [
        res.templatesCreated ? `${res.templatesCreated} template(s) created` : '',
        res.templatesUpdated ? `${res.templatesUpdated} template(s) updated` : '',
        res.dataSourcesCreated ? `${res.dataSourcesCreated} data source(s) created` : '',
        res.dataSourcesUpdated ? `${res.dataSourcesUpdated} data source(s) updated` : '',
      ].filter(Boolean)
      toast(parts.join(', ') || 'Import completed')
      setImportPackModalOpen(false)
      setPendingImportPack(null)
      await load()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Import failed')
    } finally {
      setImportingPack(false)
    }
  }

  async function toggleTemplateActive(templateIds: string[], isActive: boolean, webhookSubscriptionIds?: string[]) {
    if (templateIds.length === 0) return
    if (webhookSubscriptionIds && webhookSubscriptionIds.length === 0) {
      toast('Select at least one subscriber or send to all', 'error')
      return
    }
    setActivating(true)
    try {
      await templateApi.toggleActive(
        templateIds.map(id => ({ id, isActive })),
        webhookSubscriptionIds
      )
      toast(`Queued ${isActive ? 'activation' : 'deactivation'} for ${templateIds.length} template${templateIds.length !== 1 ? 's' : ''}`)
      setActivateModalOpen(false)
      setActivateTemplateIds([])
    } catch (e: unknown) {
      toastApiError(toast, e, 'Failed to queue active state change')
    } finally {
      setActivating(false)
    }
  }

  function toggleSelected(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectPage() {
    const visibleIds = templates.map(t => t.id)
    const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id))
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (allSelected) visibleIds.forEach(id => next.delete(id))
      else visibleIds.forEach(id => next.add(id))
      return next
    })
  }

  function clearSearch() {
    setSearch('')
    searchRef.current?.focus()
  }

  function toggleTagFilter(tag: string) {
    setSelectedTags(prev => {
      const next = new Set(prev)
      if (next.has(tag)) next.delete(tag)
      else next.add(tag)
      return next
    })
    setPage(1)
  }

  function clearTagFilter() {
    setSelectedTags(new Set())
    setPage(1)
  }

  function toggleShowDeleted(checked: boolean) {
    setShowDeleted(checked)
    setPage(1)
  }

  const from = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const to = Math.min(page * PAGE_SIZE, totalCount)
  const selectableTemplates = templates

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center gap-4 border-b border-border px-6 py-4 bg-surface flex-shrink-0">
        <div className="min-w-0">
          <h1 className="text-sm font-black tracking-wider text-ink">TEMPLATES</h1>
          <p className="text-xs text-muted font-mono mt-0.5">
            {loading ? 'Loading…' : `${totalCount} report template${totalCount !== 1 ? 's' : ''}`}
          </p>
        </div>

        {/* Search */}
        <div className="flex-1 max-w-sm relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted pointer-events-none" />
          <input
            ref={searchRef}
            className="w-full rounded-md border border-border2 bg-bg pl-8 pr-8 py-1.5 text-xs font-mono text-ink placeholder:text-muted focus:border-accent focus:outline-none transition-colors"
            placeholder="Search by name, code or ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={clearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink transition-colors"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <label className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted">
          <Checkbox
            checked={showDeleted}
            onCheckedChange={toggleShowDeleted}
            ariaLabel="Show deleted templates"
          />
          Show deleted
        </label>

        <div className="flex-1" />
        <input
          ref={importPackFileRef}
          type="file"
          accept="application/json,.json"
          className="hidden"
          aria-hidden
          onChange={onImportPackFileChosen}
        />
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className={cn(
                buttonVariants({ variant: 'ghost', size: 'icon' }),
                'text-muted hover:text-ink shrink-0'
              )}
              aria-label="Template bulk actions"
              title="More actions"
            >
              <EllipsisVertical className="h-4 w-4" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[14rem] rounded-lg border border-border bg-surface py-1 shadow-xl"
            >
              {selectableTemplates.length > 0 && (
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                  onSelect={() => toggleSelectPage()}
                >
                  <LayoutGrid className="h-3.5 w-3.5 shrink-0 text-muted" />
                  {selectableTemplates.every(t => selectedIds.has(t.id))
                    ? 'Clear selection (this page)'
                    : 'Select all on this page'}
                </DropdownMenu.Item>
              )}
              {selectableTemplates.length > 0 && <DropdownMenu.Separator className="my-1 h-px bg-border" />}
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[highlighted]:bg-bg"
                onSelect={() => {
                  requestAnimationFrame(() => openImportPackFilePicker())
                }}
              >
                <FileUp className="h-3.5 w-3.5 shrink-0 text-muted" />
                Import pack…
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                disabled={selectedIds.size === 0 || exportingPack}
                onSelect={() => exportTemplatePackJson(Array.from(selectedIds))}
              >
                {exportingPack ? (
                  <Spinner className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <Download className="h-3.5 w-3.5 shrink-0 text-muted" />
                )}
                Export selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                disabled={selectedIds.size === 0 || publishing}
                onSelect={() => openPublishModal(Array.from(selectedIds))}
              >
                <Upload className="h-3.5 w-3.5 shrink-0 text-muted" />
                {publishing ? 'Publishing…' : `Publish selected${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-orange-400 outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-orange-500/15"
                disabled={
                  selectedIds.size === 0 ||
                  activating ||
                  activeChangedSubscribersChecking ||
                  !activeChangedSubscribersAvailable
                }
                onSelect={() => openActivateModal(Array.from(selectedIds))}
              >
                <Power className="h-3.5 w-3.5 shrink-0 text-orange-400" />
                {activating ? 'Submitting…' : `Toggle active${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
        <Button onClick={() => navigate('/designer')}>
          <Plus className="h-3.5 w-3.5" /> New Template
        </Button>
      </div>

      {aggregatedTags.length > 0 && (
        <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5 border-b border-border bg-bg/40 px-6 py-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted">
            {selectedIds.size === 0 ? 'All tags' : `Tags in selection (${selectedIds.size})`}
            {selectedTags.size > 0 && ` · ${selectedTags.size} filter${selectedTags.size !== 1 ? 's' : ''}`}
          </span>
          {selectedTags.size > 0 && (
            <button
              type="button"
              onClick={clearTagFilter}
              className="rounded border border-border px-1.5 py-0.5 text-[10px] font-mono text-muted hover:border-border2 hover:text-ink transition-colors"
            >
              Clear
            </button>
          )}
          {aggregatedTags.map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => toggleTagFilter(tag)}
              className={cn(
                'rounded border px-2 py-0.5 font-mono text-[10px] transition-colors cursor-pointer',
                selectedTags.has(tag)
                  ? 'border-accent bg-accent/15 text-ink'
                  : 'border-accent/25 bg-accent/5 text-ink hover:border-accent/50 hover:bg-accent/10'
              )}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex justify-center py-20"><Spinner className="h-6 w-6" /></div>
        ) : templates.length === 0 ? (
          <EmptyState message={
            debouncedSearch
              ? `No templates match "${debouncedSearch}"`
              : selectedTags.size > 0
                ? 'No templates match the selected tags'
                : 'No templates yet. Create your first one.'
          } />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {templates.map(t => (
              <div
                key={t.id}
                className={cn(
                  'flex items-center gap-4 rounded-lg border border-border bg-surface px-5 py-4 hover:border-border2 transition-colors',
                  t.isEnabled === false && 'border-red/40 bg-red/5'
                )}
              >
                <Checkbox
                  checked={selectedIds.has(t.id)}
                  onCheckedChange={() => toggleSelected(t.id)}
                  ariaLabel={`Select ${t.name}`}
                />
                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className={cn('text-sm font-semibold text-ink truncate', t.isEnabled === false && 'text-red italic')}>
                      {t.name}
                    </p>
                    <span className="font-mono text-[10px] font-bold text-yellow bg-yellow/5 border border-yellow/20 rounded px-1.5 py-0.5 flex-shrink-0 tracking-widest">
                      {t.code}
                    </span>
                    {t.isEnabled === false && (
                      <span className="font-mono text-[10px] text-red border border-red/40 rounded px-1.5 py-0.5">
                        deleted
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(t.requiredDataSourceKeys ?? []).map(k => (
                      <span key={k} className="inline-flex items-center gap-0.5">
                        <span className="font-mono text-[10px] text-green bg-green/5 border border-green/20 rounded px-1.5 py-0.5">
                          {`{{ ${k} }}`}
                        </span>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation()
                            navigate(`/datasources?key=${encodeURIComponent(k)}`)
                          }}
                          className="rounded p-0.5 text-green/70 hover:text-green hover:bg-green/10 transition-colors"
                          title={`Open data source "${k}"`}
                          aria-label={`Open data source "${k}"`}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  {(t.tags ?? []).length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {(t.tags ?? []).map(tag => (
                        <span
                          key={tag}
                          className="rounded border border-accent/25 bg-accent/5 px-1.5 py-0.5 font-mono text-[10px] text-ink"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Date */}
                <div className="hidden sm:block flex-shrink-0 text-right">
                  <p className="text-[10px] text-muted font-mono">
                    {t.updatedAt ? new Date(t.updatedAt).toLocaleDateString() : '—'}
                  </p>
                  <p className="text-[10px] text-muted font-mono">
                    v{t.version ?? '—'}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex shrink-0 items-center">
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button
                        type="button"
                        className={cn(
                          buttonVariants({ variant: 'ghost', size: 'icon' }),
                          'text-muted hover:text-ink'
                        )}
                        aria-label={`Actions for ${t.name}`}
                        title="Actions"
                      >
                        <EllipsisVertical className="h-4 w-4" />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal>
                      <DropdownMenu.Content
                        align="end"
                        sideOffset={6}
                        className="z-50 min-w-[12.5rem] rounded-lg border border-border bg-surface py-1 shadow-xl"
                      >
                        {t.isEnabled === false ? (
                          <>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                              disabled={restoringId === t.id}
                              onSelect={() => {
                                void restoreTemplate(t)
                              }}
                            >
                              {restoringId === t.id ? (
                                <Spinner className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <RotateCcw className="h-3.5 w-3.5 shrink-0 text-green" />
                              )}
                              Undelete
                            </DropdownMenu.Item>
                            <DropdownMenu.Separator className="my-1 h-px bg-border" />
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                              disabled={cloneOpeningId === t.id || cloning}
                              onSelect={() => {
                                void openCloneModal(t.id)
                              }}
                            >
                              {cloneOpeningId === t.id ? (
                                <Spinner className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <Copy className="h-3.5 w-3.5 shrink-0 text-muted" />
                              )}
                              Clone…
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                              disabled={exportingPack}
                              onSelect={() => exportTemplatePackJson([t.id])}
                            >
                              {exportingPack ? (
                                <Spinner className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <FileJson className="h-3.5 w-3.5 shrink-0 text-muted" />
                              )}
                              Export pack…
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-green outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-green/10"
                              disabled={publishing}
                              onSelect={() => openPublishModal([t.id])}
                            >
                              <Upload className="h-3.5 w-3.5 shrink-0" />
                              Publish
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-orange-400 outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-orange-500/15"
                              disabled={
                                activating ||
                                activeChangedSubscribersChecking ||
                                !activeChangedSubscribersAvailable
                              }
                              onSelect={() => openActivateModal([t.id])}
                            >
                              <Power className="h-3.5 w-3.5 shrink-0 text-orange-400" />
                              Toggle active
                            </DropdownMenu.Item>
                          </>
                        ) : (
                          <>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[highlighted]:bg-bg"
                              onSelect={() => navigate(`/viewer/${t.id}`)}
                            >
                              <Eye className="h-3.5 w-3.5 shrink-0 text-muted" />
                              Preview
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[highlighted]:bg-bg"
                              onSelect={() => navigate(`/designer/${t.id}`)}
                            >
                              <Pencil className="h-3.5 w-3.5 shrink-0 text-muted" />
                              Edit
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-red outline-none data-[highlighted]:bg-red/10"
                              onSelect={() => setDeleteTarget({ id: t.id, name: t.name })}
                            >
                              <Trash2 className="h-3.5 w-3.5 shrink-0" />
                              Delete
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-orange-400 outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-orange-500/15"
                              disabled={
                                activating ||
                                activeChangedSubscribersChecking ||
                                !activeChangedSubscribersAvailable
                              }
                              onSelect={() => openActivateModal([t.id])}
                            >
                              <Power className="h-3.5 w-3.5 shrink-0 text-orange-400" />
                              Toggle active
                            </DropdownMenu.Item>
                            <DropdownMenu.Separator className="my-1 h-px bg-border" />
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                              disabled={cloneOpeningId === t.id || cloning}
                              onSelect={() => {
                                void openCloneModal(t.id)
                              }}
                            >
                              {cloneOpeningId === t.id ? (
                                <Spinner className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <Copy className="h-3.5 w-3.5 shrink-0 text-muted" />
                              )}
                              Clone…
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-ink outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-bg"
                              disabled={exportingPack}
                              onSelect={() => exportTemplatePackJson([t.id])}
                            >
                              {exportingPack ? (
                                <Spinner className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <FileJson className="h-3.5 w-3.5 shrink-0 text-muted" />
                              )}
                              Export pack…
                            </DropdownMenu.Item>
                            <DropdownMenu.Item
                              className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs font-semibold text-green outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-40 data-[highlighted]:bg-green/10"
                              disabled={publishing}
                              onSelect={() => openPublishModal([t.id])}
                            >
                              <Upload className="h-3.5 w-3.5 shrink-0" />
                              Publish
                            </DropdownMenu.Item>
                          </>
                        )}
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pager */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border bg-surface px-6 py-3 flex-shrink-0">
          <p className="text-[11px] text-muted font-mono">
            Showing <span className="text-ink">{from}–{to}</span> of <span className="text-ink">{totalCount}</span>
          </p>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="icon"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | '...')[]>((acc, p, i, arr) => {
                if (i > 0 && (p as number) - (arr[i - 1] as number) > 1) acc.push('...')
                acc.push(p)
                return acc
              }, [])
              .map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="w-8 text-center text-xs text-muted">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    className={`h-8 min-w-8 px-2 rounded-md text-xs font-mono transition-colors ${
                      page === p
                        ? 'bg-accent text-white font-bold'
                        : 'text-muted hover:text-ink hover:bg-border'
                    }`}
                  >
                    {p}
                  </button>
                )
              )
            }

            <Button
              variant="ghost" size="icon"
              onClick={() => setPage(p => p + 1)}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}

      {publishModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-xl overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <h2 className="text-xs font-black tracking-wider text-ink">PUBLISH TEMPLATES</h2>
                <p className="mt-0.5 text-[10px] font-mono text-muted">
                  Choose subscribers for {publishTemplateIds.length} template{publishTemplateIds.length !== 1 ? 's' : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={closePublishModal} disabled={publishing}>Cancel</Button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-5">
              <button
                type="button"
                onClick={() => {
                  setSendToAllSubscribers(true)
                  setSelectedSubscriberIds(new Set())
                }}
                className={cn(
                  'mb-3 flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                  sendToAllSubscribers ? 'border-accent bg-accent/10' : 'border-border bg-bg hover:border-border2'
                )}
              >
                <input
                  type="radio"
                  checked={sendToAllSubscribers}
                  onChange={() => {
                    setSendToAllSubscribers(true)
                    setSelectedSubscriberIds(new Set())
                  }}
                  className="h-4 w-4 accent-accent"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">Send to all subscribers</p>
                  <p className="mt-0.5 text-[10px] font-mono text-muted">
                    All enabled active subscribers for {TEMPLATE_PUBLISHED_EVENT}
                  </p>
                </div>
              </button>

              {subscribersLoading ? (
                <div className="flex justify-center py-10"><Spinner className="h-6 w-6" /></div>
              ) : subscribers.length === 0 ? (
                <EmptyState message="No enabled active subscribers for template.published." />
              ) : (
                <div className="space-y-2">
                  {subscribers.map(subscriber => {
                    const checked = !sendToAllSubscribers && selectedSubscriberIds.has(subscriber.id)
                    return (
                      <button
                        type="button"
                        key={subscriber.id}
                        onClick={() => toggleSubscriber(subscriber.id)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                          checked ? 'border-accent bg-accent/10' : 'border-border bg-bg hover:border-border2'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSubscriber(subscriber.id)}
                          onClick={e => e.stopPropagation()}
                          className="h-4 w-4 rounded border-border2 bg-bg accent-accent"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-semibold text-ink">{subscriber.tenant}</p>
                            <span className="font-mono text-[10px] font-bold text-yellow bg-yellow/5 border border-yellow/20 rounded px-1.5 py-0.5 flex-shrink-0 tracking-widest">
                              {subscriber.eventType}
                            </span>
                          </div>
                          <p className="mt-0.5 truncate text-xs text-muted">{subscriber.url}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-border px-5 py-4">
              <p className="text-[10px] font-mono text-muted">
                {sendToAllSubscribers ? 'Target: all subscribers' : `Target: ${selectedSubscriberIds.size} selected`}
              </p>
              <Button
                variant="green"
                onClick={() => publishTemplates(
                  publishTemplateIds,
                  sendToAllSubscribers ? undefined : Array.from(selectedSubscriberIds)
                )}
                disabled={publishing || subscribersLoading || (!sendToAllSubscribers && selectedSubscriberIds.size === 0)}
              >
                {publishing ? 'Publishing…' : 'Publish'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {activateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-xl overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <h2 className="text-xs font-black tracking-wider text-ink">TOGGLE TEMPLATE ACTIVE</h2>
                <p className="mt-0.5 text-[10px] font-mono text-muted">
                  Choose subscribers for {activateTemplateIds.length} template{activateTemplateIds.length !== 1 ? 's' : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeActivateModal} disabled={activating}>Cancel</Button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-5">
              <div className="mb-3 rounded-lg border border-border bg-bg px-4 py-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted">Set Active State</p>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveToggleValue(true)}
                    className={cn(
                      'rounded-md border px-3 py-2 text-xs font-semibold transition-colors',
                      activeToggleValue
                        ? 'border-green/50 bg-green/10 text-green'
                        : 'border-green/30 text-green/80 hover:border-green/50'
                    )}
                  >
                    Activate
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveToggleValue(false)}
                    className={cn(
                      'rounded-md border px-3 py-2 text-xs font-semibold transition-colors',
                      !activeToggleValue
                        ? 'border-red/50 bg-red/10 text-red'
                        : 'border-red/30 text-red/80 hover:border-red/50'
                    )}
                  >
                    Deactivate
                  </button>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setSendToAllActiveSubscribers(true)
                  setSelectedActiveSubscriberIds(new Set())
                }}
                className={cn(
                  'mb-3 flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                  sendToAllActiveSubscribers ? 'border-accent bg-accent/10' : 'border-border bg-bg hover:border-border2'
                )}
              >
                <input
                  type="radio"
                  checked={sendToAllActiveSubscribers}
                  onChange={() => {
                    setSendToAllActiveSubscribers(true)
                    setSelectedActiveSubscriberIds(new Set())
                  }}
                  className="h-4 w-4 accent-accent"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">Send to all subscribers</p>
                  <p className="mt-0.5 text-[10px] font-mono text-muted">
                    All enabled active subscribers for {TEMPLATE_ACTIVE_CHANGED_EVENT}
                  </p>
                </div>
              </button>

              {activeSubscribersLoading ? (
                <div className="flex justify-center py-10"><Spinner className="h-6 w-6" /></div>
              ) : activeSubscribers.length === 0 ? (
                <EmptyState message="No enabled active subscribers for template.active.changed." />
              ) : (
                <div className="space-y-2">
                  {activeSubscribers.map(subscriber => {
                    const checked = !sendToAllActiveSubscribers && selectedActiveSubscriberIds.has(subscriber.id)
                    return (
                      <button
                        type="button"
                        key={subscriber.id}
                        onClick={() => toggleActiveSubscriber(subscriber.id)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                          checked ? 'border-accent bg-accent/10' : 'border-border bg-bg hover:border-border2'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleActiveSubscriber(subscriber.id)}
                          onClick={e => e.stopPropagation()}
                          className="h-4 w-4 rounded border-border2 bg-bg accent-accent"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <p className="truncate text-sm font-semibold text-ink">{subscriber.tenant}</p>
                            <span className="font-mono text-[10px] font-bold text-yellow bg-yellow/5 border border-yellow/20 rounded px-1.5 py-0.5 flex-shrink-0 tracking-widest">
                              {subscriber.eventType}
                            </span>
                          </div>
                          <p className="mt-0.5 truncate text-xs text-muted">{subscriber.url}</p>
                        </div>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t border-border px-5 py-4">
              <p className="text-[10px] font-mono text-muted">
                {sendToAllActiveSubscribers ? 'Target: all subscribers' : `Target: ${selectedActiveSubscriberIds.size} selected`}
              </p>
              <Button
                variant="green"
                onClick={() => toggleTemplateActive(
                  activateTemplateIds,
                  activeToggleValue,
                  sendToAllActiveSubscribers ? undefined : Array.from(selectedActiveSubscriberIds)
                )}
                disabled={activating || activeSubscribersLoading || (!sendToAllActiveSubscribers && selectedActiveSubscriberIds.size === 0)}
              >
                {activating ? 'Submitting…' : activeToggleValue ? 'Set Active' : 'Set Inactive'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {cloneModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-xl overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <h2 className="text-xs font-black tracking-wider text-ink">CLONE TEMPLATE</h2>
                <p className="mt-0.5 text-[10px] font-mono text-muted">
                  Creates a copy with new names. Fails if any name, code, or key already exists.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeCloneModal} disabled={cloning}>
                Cancel
              </Button>
            </div>

            <div className="max-h-[60vh] space-y-4 overflow-y-auto p-5">
              <div className="space-y-2">
                <Label>Template name</Label>
                <Input
                  value={cloneName}
                  onChange={e => setCloneName(e.target.value)}
                  disabled={cloning}
                />
              </div>

              <div className="space-y-2">
                <Label>Template code</Label>
                <Input
                  value={cloneCode}
                  onChange={e => setCloneCode(e.target.value.toUpperCase())}
                  maxLength={8}
                  disabled={cloning}
                  className="font-mono uppercase"
                />
                <p className="text-[10px] text-muted font-mono">{cloneCode.length}/8</p>
              </div>

              {cloneMissingKeys.length > 0 && (
                <p className="rounded-md border border-red/30 bg-red/10 px-3 py-2 text-[11px] text-red">
                  Missing data sources: {cloneMissingKeys.join(', ')}
                </p>
              )}

              {cloneDataSourceKeys.length > 0 && (
                <div className="space-y-3">
                  <Label>Data source keys</Label>
                  {cloneDataSourceKeys.map((entry, index) => (
                    <div key={entry.sourceKey} className="space-y-1">
                      <p className="text-[10px] font-mono text-muted">
                        From <span className="text-green">{entry.sourceKey}</span>
                      </p>
                      <Input
                        value={entry.newKey}
                        onChange={e => {
                          const value = e.target.value
                          setCloneDataSourceKeys(prev =>
                            prev.map((item, i) => (i === index ? { ...item, newKey: value } : item))
                          )
                        }}
                        disabled={cloning}
                        className="font-mono"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
              <Button
                variant="green"
                onClick={() => {
                  void confirmCloneTemplate()
                }}
                disabled={cloning || cloneMissingKeys.length > 0}
              >
                {cloning ? 'Cloning…' : 'Clone'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {importPackModalOpen && pendingImportPack && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div>
                <h2 className="text-xs font-black tracking-wider text-ink">IMPORT TEMPLATE PACK</h2>
                <p className="mt-0.5 text-[10px] font-mono text-muted">
                  {pendingImportPack.templates.length} template{pendingImportPack.templates.length !== 1 ? 's' : ''},{' '}
                  {pendingImportPack.dataSources.length} data source{pendingImportPack.dataSources.length !== 1 ? 's' : ''}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeImportPackModal} disabled={importingPack}>
                Cancel
              </Button>
            </div>

            <div className="space-y-3 p-5">
              <button
                type="button"
                onClick={() => setImportConflictUpsert(true)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                  importConflictUpsert ? 'border-accent bg-accent/10' : 'border-border bg-bg hover:border-border2'
                )}
              >
                <input
                  type="radio"
                  checked={importConflictUpsert}
                  onChange={() => setImportConflictUpsert(true)}
                  className="h-4 w-4 accent-accent"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">Upsert</p>
                  <p className="mt-0.5 text-[10px] font-mono text-muted">
                    Update existing templates (by code) and data sources (by key); create missing ones.
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setImportConflictUpsert(false)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                  !importConflictUpsert ? 'border-accent bg-accent/10' : 'border-border bg-bg hover:border-border2'
                )}
              >
                <input
                  type="radio"
                  checked={!importConflictUpsert}
                  onChange={() => setImportConflictUpsert(false)}
                  className="h-4 w-4 accent-accent"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">Fail if exists</p>
                  <p className="mt-0.5 text-[10px] font-mono text-muted">
                    Abort the import if any template code or data source key already exists.
                  </p>
                </div>
              </button>
            </div>

            <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
              <Button variant="green" onClick={confirmImportPack} disabled={importingPack}>
                {importingPack ? 'Importing…' : 'Import'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="DELETE TEMPLATE"
        message={deleteTarget ? `Delete "${deleteTarget.name}"? You can restore it by enabling Show deleted.` : ''}
        loading={deleting}
        onCancel={() => {
          if (!deleting) setDeleteTarget(null)
        }}
        onConfirm={confirmDelete}
      />
    </div>
  )
}